import React, { useState, useEffect } from "react";
import { DataTable, Badge, Button, Pagination, ConfirmationModal } from "../../../components/ui";
import { TableActions } from "../../../components/ui/TableActions";
import { fmt, fmtCurrency } from "../../../lib/formatters";
import { 
  Calendar, Tag, Store, FileText, 
  Plus, RotateCcw, Eye, Edit, Ban, Check, X, UserX, UserCheck
} from "lucide-react";
import { TbTruckReturn } from "react-icons/tb";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds"; // updated by nithin: imported backgrounds for table styling

/**
 * PurchasesTable component
 * Refactored to use config-driven DataTable and reusable UI components.
 */
export const PurchasesTable = ({ 
  purchases, 
  onEdit, 
  onView, 
  onRestock, 
  onReturn, 
  onToggleStatus,
  dynamicSettings = {} 
}) => {
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [itemToToggle, setItemToToggle] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [purchases]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const { suppliers = [], stores = [] } = dynamicSettings;

  const resolveName = (value, list, type) => {
    if (!value) return type === 'vendor' ? 'Unknown Vendor' : 'Main Store';
    if (typeof value === 'object') return value.storeName || value.vendorName || value.title || value.name || value.label || value.categoryName || '—';
    
    const idStr = String(value);
    if (/^[0-9a-fA-F]{24}$/.test(idStr)) {
      const match = list.find(item => String(item._id) === idStr);
      if (match) return match.name || match.title || match.storeName || match.vendorName || match.label;
    }
    return idStr;
  };

  const columns = [

    {
      header: "Invoice Number",
      render: (item) => (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-[12px] font-medium text-slate-600">{item.invoiceNo || item.version || '—'}</span>
          {item.status === 'Queue' && (
            <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
              Queue
            </span>
          )}
        </div>
      )
    },
    {
      header: "Vendor Name",
      render: (item) => {
        const mappedVendor = item.vendorName || 
                             (typeof item.vendor === 'string' ? item.vendor : null) || 
                             (item.vendor && (item.vendor.vendorName || item.vendor.name)) || 
                             resolveName(item.vendor || item.supplier, suppliers, 'vendor');
        
        return (
          <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[150px] inline-block" title={mappedVendor}>
            {mappedVendor}
          </span>
        );
      }
    },
    {
      header: "Category",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap" title={item.category}>{item.category || '—'}</span>
    },
    {
      header: "Sub Category",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap" title={item.subCategory}>{item.subCategory || '—'}</span>
    },
    {
      header: "Article ID",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[100px] inline-block" title={item.articleId}>{item.articleId || '—'}</span>
    },
    {
      header: "Description",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[150px] inline-block" title={item.description}>{item.description || '—'}</span>
    },
    {
      header: "Information",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[150px] inline-block" title={item.information}>{item.information || '—'}</span>
    },
    {
      header: "Internal Type",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[100px] inline-block" title={item.internalType}>{item.internalType || '—'}</span>
    },
    {
      header: "Shutter Type",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[100px] inline-block" title={item.shutterType}>{item.shutterType || '—'}</span>
    },
    {
      header: "Width",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{item.width || '—'}</span>
    },
    {
      header: "Height",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{item.height || '—'}</span>
    },
    {
      header: "Depth",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{item.depth || '—'}</span>
    },
    {
      header: <span title="Unit of Measure">UOM</span>,
      render: (item) => {
        const itm = item.item || item;
        const getVal = (obj, fields) => {
          if (!obj) return null;
          if (typeof obj === 'string') return obj;
          if (typeof obj === 'object') {
            for (const f of fields) {
              if (obj[f] && typeof obj[f] === 'string') return obj[f];
            }
          }
          return null;
        };
        const safeUnit = getVal(itm.units, ['title', 'name', 'unitName', 'label']) || 
                         getVal(itm.unit, ['title', 'name', 'unitName', 'label']) || 
                         item.uom || '—';
        return <span className="text-[12px] font-medium text-slate-600 uppercase whitespace-nowrap">{safeUnit}</span>;
      }
    },
    {
      header: <span title="Minimum Order Quantity">MOQ</span>,
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{item.moq !== undefined ? item.moq : '—'}</span>
    },

    /* {
      header: "Returned Qty",
      render: (item) => {
        const returned = item.returnedQty || item.returnedQuantity || 0;
        return <span className={`text-[12px] font-medium whitespace-nowrap ${returned > 0 ? 'text-amber-600' : 'text-slate-600'}`}>{returned || '—'}</span>;
      }
    }, */
    {
      header: "Available Qty",
      render: (item) => <span className="text-[12px] font-medium text-blue-600 whitespace-nowrap">{item.quantity ?? '—'}</span>
    },
    {
      header: "Purchase Amount",
      className: "text-right",
      render: (item) => {
        const val = item.blumPrice;
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{val ? fmt(val) : '—'}</span>;
      }
    },
    {
      header: "Verve Price",
      className: "text-right",
      render: (item) => {
        const val = item.vervePrice;
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{val ? fmt(val) : '—'}</span>;
      }
    },
    {
      header: "Taxable Amount",
      className: "text-right",
      render: (item) => {
        const qty = Number(item.quantity) || 0;
        const rate = Number(item.blumPrice) || Number(item.vervePrice) || 0;
        const val = item.taxableAmount !== undefined ? item.taxableAmount : (qty * rate);
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{val ? fmt(val) : '—'}</span>;
      }
    },
    {
      header: "GST %",
      className: "text-center",
      render: (item) => {
        const val = item.gstPercentage !== undefined ? item.gstPercentage : 18;
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{`${val}%`}</span>;
      }
    },
    {
      header: "GST Amount",
      className: "text-right",
      render: (item) => {
        const qty = Number(item.quantity) || 0;
        const rate = Number(item.blumPrice) || Number(item.vervePrice) || 0;
        const taxable = item.taxableAmount !== undefined ? item.taxableAmount : (qty * rate);
        const gstP = item.gstPercentage !== undefined ? item.gstPercentage : 18;
        const val = item.taxAmount !== undefined ? item.taxAmount : (taxable * gstP / 100);
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">{val ? fmt(val) : '—'}</span>;
      }
    },
    {
      header: "Net Amount",
      className: "text-right",
      render: (item) => {
        const qty = Number(item.quantity) || 0;
        const rate = Number(item.blumPrice) || Number(item.vervePrice) || 0;
        const taxable = item.taxableAmount !== undefined ? item.taxableAmount : (qty * rate);
        const gstP = item.gstPercentage !== undefined ? item.gstPercentage : 18;
        const tax = item.taxAmount !== undefined ? item.taxAmount : (taxable * gstP / 100);
        const val = item.netAmount !== undefined ? item.netAmount : (taxable + tax);
        return <span className="text-[12px] font-bold text-emerald-600 whitespace-nowrap">{val ? fmt(val) : '—'}</span>;
      }
    },
    {
      header: "Image",
      render: (item) => {
        if (item.image) {
           const isSelected = selectedImage === item._id;
           return (
            <img 
              src={item.image} 
              alt="Item" 
              onClick={(e) => { e.stopPropagation(); setSelectedImage(isSelected ? null : item._id); }}
              className={`${isSelected ? 'w-[150px] h-[150px]' : 'w-[32px] h-[32px]'} object-contain rounded shadow-sm border border-slate-200 cursor-pointer transition-all bg-white`} 
            />
           );
        } else {
           return <span className="text-[10px] text-slate-400 font-medium tracking-wide whitespace-nowrap">No Image</span>;
        }
      }
    },
    {
      header: "Remarks",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap truncate max-w-[150px] inline-block" title={item.remark || item.remarks}>{item.remark || item.remarks || '—'}</span>
    },
    {
      header: "Date",
      render: (item) => (
        <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
          {item.invoiceDate || item.date || item.createdAt ? new Date(item.invoiceDate || item.date || item.createdAt).toLocaleDateString('en-IN') : '—'}
        </span>
      )
    },
    {
      header: "Version",
      render: (item) => {
        let v = item.version || '—';
        if (typeof v === 'string' && v.toUpperCase().startsWith('V-')) {
          v = v.substring(2);
        }
        return <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap inline-block">{v}</span>;
      }
    },
    {
      header: "Actions",
      className: "w-28",
      render: (item) => {
        const isInactiveStatus = item.status === 'Inactive';
        return (
          <TableActions
            onView={!isInactiveStatus ? () => onView && onView(item) : null}
            onEdit={!isInactiveStatus ? () => onEdit && onEdit(item) : null}
            onToggle={() => setItemToToggle(item)}
            isActive={!isInactiveStatus}
            disabled={false}
          />
        );
      }
    }
  ];

  const sorted = [...(purchases || [])].sort((a, b) => {
    const da = new Date(a.invoiceDate || a.createdAt);
    const db = new Date(b.invoiceDate || b.createdAt);
    return (isNaN(db) ? 0 : db) - (isNaN(da) ? 0 : da);
  });
  const currentData = sorted.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((purchases || []).length / itemsPerPage), 1);

  return (
    <div className="card rounded-none overflow-hidden shadow-sm border-slate-100">
      <DataTable 
        columns={columns} 
        data={currentData} 
        getRowClassName={(item) => `hover:bg-slate-50/50 transition-all ${(item.status === 'Inactive' || item.status === 'INACTIVE') ? 'opacity-40 grayscale' : ''}`}
      />
      
      {(purchases || []).length > itemsPerPage && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Selected image full screen modal removed since we are expanding inline */}

      <ConfirmationModal
        isOpen={!!itemToToggle}
        onClose={() => setItemToToggle(null)}
        onConfirm={() => {
          if (itemToToggle) {
            onToggleStatus && onToggleStatus(itemToToggle);
            setItemToToggle(null);
          }
        }}
        title={`${itemToToggle?.status === 'Inactive' ? 'Enable' : 'Disable'} Item`}
        message={`Are you sure you want to ${itemToToggle?.status === 'Inactive' ? 'enable' : 'disable'} this item?`}
        confirmLabel={itemToToggle?.status === 'Inactive' ? 'Enable' : 'Disable'}
        type={itemToToggle?.status === 'Inactive' ? 'success' : 'danger'}
      />
    </div>
  );
};

